// src/pages/portal/PortalArticuloPage.tsx
import { useState } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortalArticulo, useMarcarArticuloUtil as useMarcarPortalArticuloUtil } from '@/hooks/queries';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Skeleton,
} from '@/components/ui';
import { ArrowLeft, ThumbsUp, ThumbsDown, Eye, Calendar, Folder } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function ArticuloSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-48" />
      <Skeleton className="h-96" />
    </div>
  );
}

export default function PortalArticuloPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feedbackGiven, setFeedbackGiven] = useState<'util' | 'no_util' | null>(null);

  const { data: articulo, isLoading, isError } = usePortalArticulo(id);
  const marcarUtil = useMarcarPortalArticuloUtil();

  const handleFeedback = (util: boolean) => {
    if (!id || feedbackGiven) return;

    marcarUtil.mutate(
      { id, util },
      {
        onSuccess: () => setFeedbackGiven(util ? 'util' : 'no_util'),
      }
    );
  };

  if (isLoading) {
    return <ArticuloSkeleton />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error al cargar el articulo</p>
        <Button className="mt-4" onClick={() => navigate('/portal/conocimiento')}>
          Volver
        </Button>
      </div>
    );
  }

  if (!articulo) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">
          Articulo no encontrado
        </p>
        <Button className="mt-4" onClick={() => navigate('/portal/conocimiento')}>
          Volver
        </Button>
      </div>
    );
  }

  const tipoBadgeVariant = (tipo: string) => {
    switch (tipo?.toLowerCase()) {
      case 'guia':
        return 'info';
      case 'faq':
        return 'warning';
      case 'tutorial':
        return 'success';
      case 'documentacion':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/portal/conocimiento')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>

      {/* Articulo */}
      <Card>
        <CardHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                #{articulo.numero}
              </span>
              <Badge variant={tipoBadgeVariant(articulo.tipo)}>
                {articulo.tipo}
              </Badge>
              {articulo.categoria && (
                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <Folder className="w-4 h-4" />
                  {articulo.categoria.nombre}
                </div>
              )}
            </div>
            <CardTitle className="text-2xl">{articulo.titulo}</CardTitle>
            {articulo.resumen && (
              <p className="text-gray-600 dark:text-gray-400">
                {articulo.resumen}
              </p>
            )}
            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {articulo.vistas} vistas
              </div>
              <div className="flex items-center gap-1">
                <ThumbsUp className="w-4 h-4" />
                {articulo.votos_util} lo encontraron util
              </div>
              {articulo.fecha_publicacion && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(articulo.fecha_publicacion), 'dd MMM yyyy', { locale: es })}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Contenido del articulo */}
          <div
            className="prose dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(articulo.contenido || '') }}
          />
        </CardContent>
      </Card>

      {/* Feedback */}
      <Card>
        <CardContent className="py-6">
          <div className="text-center">
            {feedbackGiven ? (
              <div className="text-green-600 dark:text-green-400">
                <p className="font-medium">Gracias por tu feedback</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Tu opinion nos ayuda a mejorar nuestra documentacion
                </p>
              </div>
            ) : (
              <>
                <p className="font-medium text-gray-900 dark:text-white mb-4">
                  Te resulto util este articulo?
                </p>
                <div className="flex justify-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => handleFeedback(true)}
                    disabled={marcarUtil.isPending}
                    className="flex items-center gap-2"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    Si, me ayudo
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleFeedback(false)}
                    disabled={marcarUtil.isPending}
                    className="flex items-center gap-2"
                  >
                    <ThumbsDown className="w-4 h-4" />
                    No me ayudo
                  </Button>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Link a crear ticket */}
      <Card>
        <CardContent className="py-6">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No encontraste lo que buscabas?
            </p>
            <Button onClick={() => navigate('/portal/tickets/nuevo')}>
              Crear un ticket de soporte
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
